import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import NormalProgressBar from '@/components/common/bar/NormalProgressBar'
import MainTopBar from '@/components/common/bar/MainTopBar'
import AnswerButton from '@/components/typeTest/AnswerButton'
import WideButton from '@/components/common/button/WideButton'
import { useGetTypeTestQuestions } from '@/hooks/queries/typeTest'
import { useSendTypeTestAnswersMutation } from '@/hooks/queries/typeTest/mutation'
import LoadingScreen from '@/components/typeTest/LoadingScreen'
import PerformanceEvaluation from '@/components/typeTest/PerformanceEvaluation'
import type { PerformanceType, TypeTestDiagnosisRequest } from '@/api/model/typeTestType'
import ExitTypeTestConfirmModal from '@/components/typeTest/ExitTypeTestConfirmModal'
import { useQueryClient } from '@tanstack/react-query'
import { useUserStore } from '@/store/userStore'

const DEFAULT_TYPE_TEST_ANSWER = {
  performance: '상',
  answers: Object.fromEntries(Array.from({ length: 25 }, (_, i) => [i + 1, undefined]))
}

export default function TypeTestQuestionPage() {
  const navigate = useNavigate()
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<TypeTestDiagnosisRequest | any>(DEFAULT_TYPE_TEST_ANSWER)
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true)
  const [showPerformanceEvaluation, setShowPerformanceEvaluation] = useState(false)
  const [showExitModal, setShowExitModal] = useState(false)
  const { userInfo } = useUserStore()
  const queryClient = useQueryClient()

  const { data: typeTestQuestions } = useGetTypeTestQuestions()
  const { mutate: sendTypeTestAnswers, isPending: isSendingTypeTestAnswers } = useSendTypeTestAnswersMutation()

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    } else {
      navigate(-1)
    }
  }

  const handleNext = () => {
    if (currentQuestionIndex < (typeTestQuestions?.total_count ?? 0) - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1)
      }, 200) // 약간의 딜레이로 선택 피드백 제공
    } else {
      // 마지막 질문 완료 - 성과 평가 화면으로 전환
      setShowPerformanceEvaluation(true)
    }
  }

  const handlePerformanceComplete = (performance: PerformanceType) => {
    const finalAnswers = { ...answers, performance }
    sendTypeTestAnswers(finalAnswers, {
      onSuccess: responseData => {
        navigate('/type-test/result', { state: { typeTestResult: responseData } })
        queryClient.invalidateQueries({ queryKey: ['userInfo', userInfo.user_id] })
      }
    })
  }

  const handlePerformanceBack = () => {
    setShowPerformanceEvaluation(false)
  }

  useEffect(() => {
    // 현재 질문에 대한 답변이 이미 선택되어 있는지 확인
    const currentAnswer = answers.answers[(currentQuestionIndex + 1) as keyof TypeTestDiagnosisRequest['answers']]
    setIsNextButtonDisabled(!currentAnswer || currentAnswer === undefined)
  }, [currentQuestionIndex, answers])

  if (isSendingTypeTestAnswers) {
    return <LoadingScreen />
  }

  if (showPerformanceEvaluation) {
    return <PerformanceEvaluation onComplete={handlePerformanceComplete} onBack={handlePerformanceBack} />
  }

  return (
    <div className='min-h-screen'>
      {/* 헤더 */}
      <MainTopBar
        hasBackButton={true}
        leftSide={
          <div className='flex items-center gap-2'>
            <h1 className='text-header-title font-semibold text-text-header'>타입검사</h1>
          </div>
        }
        handleClickBackButton={() => setShowExitModal(true)}
      />

      {/* 메인 콘텐츠 */}
      <div className='px-3 pb-8 pt-3'>
        <div className='bg-white rounded-2xl shadow-lg overflow-hidden min-h-[calc(100vh-200px)]'>
          <div className='p-6'>
            {/* 프로그레스 바 */}
            <div className='mb-8'>
              <div className='text-sm text-gray-500 mb-2 text-center'>
                {currentQuestionIndex + 1} / {typeTestQuestions?.total_count ?? 0}
              </div>
              <NormalProgressBar
                normalProgressBarStyle={{
                  value: currentQuestionIndex + 1,
                  maxValue: typeTestQuestions?.total_count ?? 0
                }}
              />
            </div>

            {/* 질문 */}
            <div className='mb-12'>
              <h1 className='text-[20px] font-bold text-gray-800 leading-relaxed'>
                {currentQuestionIndex + 1}. {typeTestQuestions?.questions[currentQuestionIndex]?.question}
              </h1>
            </div>

            {/* 답변 옵션 */}
            <div className='space-y-3'>
              <AnswerButton
                label='YES'
                isSelected={
                  answers.answers[(currentQuestionIndex + 1) as keyof TypeTestDiagnosisRequest['answers']] === 'YES'
                }
                onClick={() => {
                  const newAnswers = { ...answers, answers: { ...answers.answers } }
                  newAnswers.answers[(currentQuestionIndex + 1) as keyof TypeTestDiagnosisRequest['answers']] = 'YES'
                  setAnswers(newAnswers)
                  setIsNextButtonDisabled(false)
                }}
              />
              <AnswerButton
                label='NO'
                isSelected={
                  answers.answers[(currentQuestionIndex + 1) as keyof TypeTestDiagnosisRequest['answers']] === 'NO'
                }
                onClick={() => {
                  const newAnswers = { ...answers, answers: { ...answers.answers } }
                  newAnswers.answers[(currentQuestionIndex + 1) as keyof TypeTestDiagnosisRequest['answers']] = 'NO'
                  setAnswers(newAnswers)
                  setIsNextButtonDisabled(false)
                }}
              />
              <AnswerButton
                label='모르겠다'
                isSelected={
                  answers.answers[(currentQuestionIndex + 1) as keyof TypeTestDiagnosisRequest['answers']] === 'UNKNOWN'
                }
                onClick={() => {
                  const newAnswers = { ...answers, answers: { ...answers.answers } }
                  newAnswers.answers[(currentQuestionIndex + 1) as keyof TypeTestDiagnosisRequest['answers']] =
                    'UNKNOWN'
                  setAnswers(newAnswers)
                  setIsNextButtonDisabled(false)
                }}
              />
            </div>
          </div>
        </div>
        <div className='flex gap-3 mt-4'>
          <WideButton
            className={`box-border ${currentQuestionIndex === 0 ? 'opacity-50' : ''}`}
            buttonText='이전'
            handleClick={handleBack}
            isDisabled={currentQuestionIndex === 0}
          />
          <WideButton
            className={`box-border ${isNextButtonDisabled ? 'opacity-50' : ''}`}
            buttonText='다음'
            handleClick={handleNext}
            isDisabled={isNextButtonDisabled}
          />
        </div>
      </div>
      {showExitModal && (
        <ExitTypeTestConfirmModal
          onExit={() => {
            setShowExitModal(false)
            navigate(-1)
          }}
          onClose={() => setShowExitModal(false)}
        />
      )}
    </div>
  )
}
